import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import ConfirmarModal from '../components/ConfirmarModal';
import useFiltroLembrado from '../hooks/useFiltroLembrado';
import '../styles/painelOperador.css';
import '../styles/gestaoBpoRh.css';
import '../styles/fiscal.css';
import '../styles/fiscalModerno.css';

// Fiscal → Monitor de NFS-e (port do ZADRA). Empresas habilitadas baixam as notas do
// Ambiente de Dados Nacional (ADN) por NSU com o próprio certificado A1; São Paulo é
// uma ordem à parte, por período. Ordens entram na fila (job a cada 15–30 min) ou são
// processadas na hora pelo botão "Processar agora" — o NSU é salvo a cada lote, então
// uma ordem que não termina na hora continua de onde parou.

const API = process.env.REACT_APP_API_BASE;
const URL_MON = `${API}/api/fiscal/monitor`;
const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('access')}` });

const ABAS = [
  { id: 'empresas', ico: 'fa-building', txt: 'Empresas & ordens' },
  { id: 'grupos', ico: 'fa-layer-group', txt: 'Grupos' },
  { id: 'documentos', ico: 'fa-file-code', txt: 'Documentos' },
  { id: 'retroativas', ico: 'fa-clock-rotate-left', txt: 'Notas retroativas' },
];
const PERIODICIDADES = [['Diaria', 'Diária'], ['Semanal', 'Semanal'], ['Quinzenal', 'Quinzenal'], ['Mensal', 'Mensal']];
const SELO_STATUS = {
  Pendente: ['neutro', 'fa-hourglass-half'],
  Processando: ['alerta', 'fa-spinner fa-spin'],
  'Concluído': ['ok', 'fa-circle-check'],
  Erro: ['erro', 'fa-circle-xmark'],
};
const SELO_CERT = {
  vencido: ['erro', 'fa-circle-xmark', 'vencido'],
  vence_30d: ['alerta', 'fa-clock', 'vence em breve'],
  arquivo_pendente: ['erro', 'fa-upload', 'sem arquivo'],
  sem_validade: ['alerta', 'fa-calendar-xmark', 'sem validade'],
  ok: ['ok', 'fa-circle-check', 'em dia'],
};
const TIPOS = { emitidas: 'Emitida', recebidas: 'Recebida', eventos: 'Evento' };
const PROVEDORES = { nacional: 'Nacional', sao_paulo: 'São Paulo' };

const fmtCnpj = (c) => (c && c.length === 14 ? c.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5') : c || '—');

// Copia o CNPJ só com os dígitos (sem pontuação) e confirma com um check por ~1 s
function CopiarCnpj({ cnpj }) {
  const [ok, setOk] = useState(false);
  const digitos = (cnpj || '').replace(/\D/g, '');
  if (digitos.length !== 14) return null;
  const copiar = () => {
    const feito = () => { setOk(true); setTimeout(() => setOk(false), 1200); };
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(digitos).then(feito).catch(() => {});
    } else {
      const area = document.createElement('textarea');
      area.value = digitos;
      document.body.appendChild(area);
      area.select();
      document.execCommand('copy');
      area.remove();
      feito();
    }
  };
  return (
    <button type="button" className="btn btn-sm btn-link p-0 ms-1 align-baseline" style={{ fontSize: '.75rem', lineHeight: 1 }}
      title="Copiar o CNPJ sem pontuação" aria-label="Copiar o CNPJ sem pontuação" onClick={copiar}>
      <i className={`fas ${ok ? 'fa-check text-success' : 'fa-copy'}`} aria-hidden="true"></i>
    </button>
  );
}
const semAcento = (s) => (s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
const fmtComp = (c) => (c && c.length === 6 ? `${c.slice(4)}/${c.slice(0, 4)}` : 'Sem data');
const fmtValor = (v) => (v == null ? '—' : Number(v).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }));
const fmtNum = (n) => Number(n || 0).toLocaleString('pt-BR');
const msgErro = (e, padrao) => e.response?.data?.mensagem || e.response?.data?.erro || padrao;

function mesAnterior() {
  const hoje = new Date();
  const ini = new Date(hoje.getFullYear(), hoje.getMonth() - 1, 1);
  const fim = new Date(hoje.getFullYear(), hoje.getMonth(), 0);
  const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  return [iso(ini), iso(fim)];
}

async function baixarArquivo(url, params, nomePadrao) {
  const r = await axios.get(url, { params, headers: headers(), responseType: 'blob' });
  const disp = r.headers['content-disposition'] || '';
  const m = disp.match(/filename="?([^"]+)"?/);
  const link = document.createElement('a');
  link.href = window.URL.createObjectURL(r.data);
  link.download = m ? m[1] : nomePadrao;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(link.href);
}

// Download por POST (ex.: ZIP dos selecionados)
async function baixarArquivoPost(url, corpo, nomePadrao) {
  const r = await axios.post(url, corpo, { headers: headers(), responseType: 'blob' });
  const disp = r.headers['content-disposition'] || '';
  const m = disp.match(/filename="?([^"]+)"?/);
  const link = document.createElement('a');
  link.href = window.URL.createObjectURL(r.data);
  link.download = m ? m[1] : nomePadrao;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(link.href);
}

// Em download (responseType blob) o erro do servidor chega como Blob: lê o JSON dele.
// Sem resposta (rede, servidor reiniciando) ou resposta que não é JSON, diz o motivo
// real em vez de só a mensagem padrão.
async function erroDeBlob(e, padrao) {
  console.error(padrao, e);
  if (!e.response) return `${padrao}: o servidor não respondeu (${e.message || 'falha de rede'}). Tente de novo.`;
  try {
    const txt = await e.response.data.text();
    const corpo = JSON.parse(txt);
    return corpo.mensagem || corpo.detail || `${padrao} (HTTP ${e.response.status})`;
  } catch {
    return `${padrao} (HTTP ${e.response.status})`;
  }
}

function SeloStatus({ prov }) {
  if (!prov?.status) return <span className="fz-selo neutro"><i className="fas fa-minus"></i>nunca rodou</span>;
  const [cls, ico] = SELO_STATUS[prov.status] || SELO_STATUS.Pendente;
  return <span className={`fz-selo ${cls}`} title={prov.erro || undefined}><i className={`fas ${ico}`}></i>{prov.status}</span>;
}

function SeloCertificado({ cert, procuracao }) {
  // sem A1 próprio válido, mas o ADN é lido com o certificado do escritório (procuração eletrônica)
  const proc = procuracao && (
    <span className="fz-selo ok ms-1" title="Sem A1 próprio: a leitura nacional usa o certificado do escritório, via procuração eletrônica cadastrada na Receita">
      <i className="fas fa-user-shield"></i>procuração
    </span>
  );
  if (!cert) return <>{!procuracao && <span className="fz-selo erro"><i className="fas fa-minus"></i>sem certificado</span>}{proc}</>;
  const [cls, ico, txt] = SELO_CERT[cert.situacao] || SELO_CERT.sem_validade;
  return <><span className={`fz-selo ${cls}`} title={cert.validade ? `validade ${cert.validade}` : undefined}><i className={`fas ${ico}`}></i>{txt}</span>{proc}</>;
}

function Modal({ titulo, subtitulo, icone, onFechar, travado, largo, children, rodape }) {
  return (
    <div className="modal show d-block" tabIndex="-1" style={{ background: 'rgba(0,0,0,.45)' }}>
      <div className={`modal-dialog modal-dialog-centered ${largo ? 'modal-lg' : ''}`}>
        <div className="modal-content shadow-lg">
          <div className="modal-header" style={{ backgroundColor: '#ffffff', borderBottom: '1px solid #e9ecef' }}>
            <div>
              <h5 className="modal-title mb-0 fw-bold" style={{ color: '#212529' }}><i className={`fas ${icone} me-2`} style={{ color: '#00b5cc' }}></i>{titulo}</h5>
              {subtitulo && <small style={{ color: '#6c757d' }}>{subtitulo}</small>}
            </div>
            <button type="button" className="btn-close" onClick={onFechar} disabled={travado}></button>
          </div>
          <div className="modal-body">{children}</div>
          <div className="modal-footer bg-light">{rodape}</div>
        </div>
      </div>
    </div>
  );
}

function ResumoResultado({ r }) {
  if (!r || r.nsu_inicial == null) return null;
  return (
    <span className="fz-sub">
      {fmtNum(r.novos)} novo(s) · {fmtNum(r.existentes)} já existente(s){r.conflitos ? ` · ${r.conflitos} conflito(s)` : ''}
      {r.retroativas ? ` · ${r.retroativas} retroativa(s)` : ''} · NSU {r.nsu_inicial} → {r.ultimo_nsu} · {r.lotes} lote(s)
    </span>
  );
}

// ---------------------------------------------------------------------------
// Aba: Empresas & ordens
// ---------------------------------------------------------------------------
function AbaEmpresas({ dados, grupos, carregar, setErro, setMsg, abrirNovaOrdem, ordens, carregarOrdens, processar, processando }) {
  // filtros lembrados: trocar de aba/tela e voltar mantém a seleção (sessionStorage)
  const [busca, setBusca] = useFiltroLembrado('fiscalMonitor.busca', '');
  const [filtro, setFiltro] = useFiltroLembrado('fiscalMonitor.kpi', '');
  const [fCert, setFCert] = useFiltroLembrado('fiscalMonitor.cert', '');
  const [fNac, setFNac] = useFiltroLembrado('fiscalMonitor.nacional', '');
  const [fSp, setFSp] = useFiltroLembrado('fiscalMonitor.sp', '');
  const [fGrupo, setFGrupo] = useFiltroLembrado('fiscalMonitor.grupo', '');
  const [fXml, setFXml] = useFiltroLembrado('fiscalMonitor.xml', '');
  const [mostrarDesab, setMostrarDesab] = useFiltroLembrado('fiscalMonitor.desabilitadas', false);
  const [habilitar, setHabilitar] = useState(null);   // { cod, nsu, busca }
  const [editNsu, setEditNsu] = useState(null);       // { empresa, nsu }
  const [desabilitar, setDesabilitar] = useState(null);
  const [salvando, setSalvando] = useState(false);

  const empresas = useMemo(() => dados?.empresas || [], [dados]);
  const contagem = useMemo(() => {
    const c = { habilitadas: 0, Pendente: 0, Processando: 0, Erro: 0, 'Concluído': 0 };
    empresas.filter((e) => e.habilitada).forEach((e) => {
      c.habilitadas += 1;
      if (e.nacional.status in c) c[e.nacional.status] += 1;
    });
    return c;
  }, [empresas]);

  const lista = useMemo(() => {
    const termo = semAcento(busca.trim());
    return empresas.filter((e) => {
      if (!e.habilitada && !mostrarDesab) return false;
      if (filtro && e.nacional.status !== filtro && e.sp.status !== filtro) return false;
      if (fCert === 'procuracao') {
        if (!e.procuracao) return false;
      } else if (fCert && (fCert === 'sem' ? !!e.certificado : (e.certificado?.situacao || '#') !== fCert)) return false;
      if (fNac && (fNac === 'nunca' ? !!e.nacional.status : e.nacional.status !== fNac)) return false;
      if (fSp) {
        if (fSp === 'sem' && (e.sao_paulo || e.sp.status)) return false;
        if (fSp === 'ccm' && !(e.sao_paulo && !e.sp.status)) return false;
        if (fSp !== 'sem' && fSp !== 'ccm' && e.sp.status !== fSp) return false;
      }
      if (fGrupo && (fGrupo === 'nenhum' ? e.grupos.length > 0 : !e.grupos.some((g) => String(g.id) === fGrupo))) return false;
      if (fXml && (fXml === 'com') !== (Number(e.documentos) > 0)) return false;
      return !termo || semAcento(e.cliente).includes(termo) || String(e.cod) === termo || (e.cnpj || '').includes(busca.replace(/\D/g, '') || '#');
    });
  }, [empresas, busca, filtro, fCert, fNac, fSp, fGrupo, fXml, mostrarDesab]);

  const filtrosAtivos = !!(busca.trim() || filtro || fCert || fNac || fSp || fGrupo || fXml);
  const limparFiltros = () => { setBusca(''); setFiltro(''); setFCert(''); setFNac(''); setFSp(''); setFGrupo(''); setFXml(''); };

  const disponiveis = useMemo(() => {
    const termo = semAcento(habilitar?.busca || '');
    return (dados?.disponiveis || []).filter((e) => !termo || semAcento(e.cliente).includes(termo) || String(e.cod) === termo).slice(0, 200);
  }, [dados, habilitar]);

  // código exato ou um único resultado já seleciona a empresa; a escolhida que sai do filtro é desmarcada
  const filtrarHabilitar = (texto) => {
    const termo = semAcento(texto.trim());
    const lista = (dados?.disponiveis || []).filter((e) => !termo || semAcento(e.cliente).includes(termo) || String(e.cod) === termo);
    const exato = lista.find((e) => String(e.cod) === texto.trim());
    setHabilitar((s) => {
      let cod = s.cod;
      if (exato) cod = String(exato.cod);
      else if (lista.length === 1) cod = String(lista[0].cod);
      else if (cod && !lista.some((e) => String(e.cod) === String(cod))) cod = '';
      return { ...s, busca: texto, cod };
    });
  };

  const salvarHabilitar = () => {
    setSalvando(true);
    axios.post(`${URL_MON}/empresas/`, { cod: habilitar.cod, ultimo_nsu: habilitar.nsu === '' ? 0 : Number(habilitar.nsu) }, { headers: headers() })
      .then((r) => { setMsg(r.data.mensagem); setHabilitar(null); carregar(); })
      .catch((e) => setErro(msgErro(e, 'Não foi possível habilitar a empresa')))
      .finally(() => setSalvando(false));
  };

  const patch = (cod, corpo, depois) => {
    setSalvando(true);
    axios.patch(`${URL_MON}/empresas/${cod}/`, corpo, { headers: headers() })
      .then((r) => { setMsg(r.data.mensagem); depois(); carregar(); })
      .catch((e) => setErro(msgErro(e, 'Não foi possível salvar')))
      .finally(() => setSalvando(false));
  };

  const KPIS = [
    ['habilitadas', 'Habilitadas', 'fa-building', 'c-navy', ''],
    ['Pendente', 'Na fila', 'fa-hourglass-half', 'c-navy', 'Pendente'],
    ['Processando', 'Processando', 'fa-spinner', 'c-warn', 'Processando'],
    ['Erro', 'Com erro', 'fa-circle-xmark', 'c-bad', 'Erro'],
    ['Concluído', 'Concluídas', 'fa-circle-check', 'c-ok', 'Concluído'],
  ];

  return (
    <>
      <div className="po-kpis" style={{ gridTemplateColumns: 'repeat(5, minmax(0, 1fr))' }}>
        {KPIS.map(([chave, txt, ico, cls, f]) => (
          <button key={chave} type="button" className={`po-kpi ${cls} ${contagem[chave] ? '' : 'po-zero'}`}
            onClick={() => setFiltro((x) => (x === f ? '' : f))} style={f && filtro === f ? { borderColor: '#00b5cc', boxShadow: '0 0 0 2px rgba(0,181,204,.35)' } : undefined}>
            <div className="po-kpi-rotulo"><i className={`fas ${ico}`}></i>{txt}</div>
            <div className="po-kpi-valor">{contagem[chave] || 0}</div>
          </button>
        ))}
      </div>

      <div className="fz-filtros">
        <select className="fz-sel" value={fGrupo} onChange={(e) => setFGrupo(e.target.value)} aria-label="Grupo" title="Grupo do Monitor">
          <option value="">Grupo: todos</option>
          {grupos.map((g) => <option key={g.id} value={String(g.id)}>{g.nome}</option>)}
          <option value="nenhum">Sem grupo</option>
        </select>
        <label className="po-toggle"><input type="checkbox" checked={mostrarDesab} onChange={(e) => setMostrarDesab(e.target.checked)} />Mostrar desabilitadas</label>
        {filtrosAtivos && (
          <button type="button" className="btn btn-sm btn-link text-decoration-none" onClick={limparFiltros} title="Limpar busca e filtros">
            <i className="fas fa-filter-circle-xmark me-1"></i>Limpar filtros
          </button>
        )}
        <button type="button" className="gr-btn" onClick={() => setHabilitar({ cod: '', nsu: '0', busca: '' })}><i className="fas fa-plus"></i>Habilitar empresa</button>
        <button type="button" className="gr-btn" onClick={() => abrirNovaOrdem({ modo: 'grupo' })} disabled={!grupos.length}><i className="fas fa-layer-group"></i>Ordem por grupo</button>
      </div>

      <section className="po-card po-enter fz-topo-fixo">
        <header className="po-card-head">
          <h3><i className="fas fa-building" aria-hidden="true"></i>Empresas no Monitor</h3>
          <span className="badge bg-light text-dark">
            {filtrosAtivos ? `${lista.length} de ${empresas.filter((e) => e.habilitada || mostrarDesab).length}` : lista.length}
          </span>
        </header>
        <div className="po-card-body">
          {/* sem rolagem interna: a lista rola com a página e o cabeçalho + filtros ficam fixos no topo */}
          <div style={{ minHeight: 420 }}>
            <table className="table table-hover po-table align-middle mb-0">
              <thead>
                <tr><th>Empresa</th><th>Certificado</th><th className="text-end">Último NSU</th><th>Nacional</th><th>São Paulo</th><th className="text-end">XMLs</th><th></th></tr>
                <tr className="fz-th-filtro">
                  <th>
                    <input className="fz-filtro-campo" placeholder="Filtrar por nome, código ou CNPJ" value={busca} onChange={(e) => setBusca(e.target.value)} aria-label="Filtrar empresa" />
                  </th>
                  <th>
                    <select className="fz-sel fz-filtro-campo" value={fCert} onChange={(e) => setFCert(e.target.value)} aria-label="Filtrar certificado">
                      <option value="">Todos</option>
                      <option value="ok">Em dia</option>
                      <option value="vence_30d">Vence em breve</option>
                      <option value="vencido">Vencido</option>
                      <option value="arquivo_pendente">Sem arquivo</option>
                      <option value="sem_validade">Sem validade</option>
                      <option value="sem">Sem certificado</option>
                      <option value="procuracao">Via procuração</option>
                    </select>
                  </th>
                  <th></th>
                  <th>
                    <select className="fz-sel fz-filtro-campo" value={fNac} onChange={(e) => setFNac(e.target.value)} aria-label="Filtrar status Nacional">
                      <option value="">Todos</option>
                      <option value="Pendente">Pendente</option>
                      <option value="Processando">Processando</option>
                      <option value="Concluído">Concluído</option>
                      <option value="Erro">Erro</option>
                      <option value="nunca">Nunca rodou</option>
                    </select>
                  </th>
                  <th>
                    <select className="fz-sel fz-filtro-campo" value={fSp} onChange={(e) => setFSp(e.target.value)} aria-label="Filtrar São Paulo">
                      <option value="">Todas</option>
                      <option value="ccm">CCM cadastrado</option>
                      <option value="Pendente">Pendente</option>
                      <option value="Processando">Processando</option>
                      <option value="Concluído">Concluído</option>
                      <option value="Erro">Erro</option>
                      <option value="sem">Sem CCM</option>
                    </select>
                  </th>
                  <th>
                    <select className="fz-sel fz-filtro-campo" value={fXml} onChange={(e) => setFXml(e.target.value)} aria-label="Filtrar XMLs">
                      <option value="">Todos</option>
                      <option value="com">Com XMLs</option>
                      <option value="sem">Sem XMLs</option>
                    </select>
                  </th>
                  <th className="text-end">
                    {filtrosAtivos && (
                      <button type="button" className="btn btn-sm btn-link p-0 text-decoration-none" onClick={limparFiltros} title="Limpar busca e filtros">
                        <i className="fas fa-filter-circle-xmark"></i>
                      </button>
                    )}
                  </th>
                </tr>
              </thead>
              <tbody>
                {lista.map((e) => {
                  const ordemNac = e.nacional.ordem;
                  const ordemSp = e.sp.ordem;
                  const pendente = [ordemNac, ordemSp].find((o) => o && o.status === 'pendente');
                  return (
                    <tr key={e.cod} style={e.habilitada ? undefined : { opacity: 0.6 }}>
                      <td>
                        <div className="fw-semibold">{e.cliente}{!e.habilitada && <span className="fz-selo neutro ms-2">desabilitada</span>}</div>
                        <div className="fz-sub fz-mono">{e.cod} · {fmtCnpj(e.cnpj)}<CopiarCnpj cnpj={e.cnpj} /></div>
                        {e.grupos.length > 0 && <div className="fz-sub">{e.grupos.map((g) => g.nome).join(' · ')}</div>}
                      </td>
                      <td><SeloCertificado cert={e.certificado} procuracao={e.procuracao} /></td>
                      <td className="text-end fz-mono">
                        {fmtNum(e.ultimo_nsu)}
                        <button className="btn btn-sm btn-link p-0 ms-2" title="Editar NSU" onClick={() => setEditNsu({ empresa: e, nsu: String(e.ultimo_nsu) })}><i className="fas fa-pen"></i></button>
                      </td>
                      <td>
                        <SeloStatus prov={e.nacional} />
                        <div className="fz-sub">{processando[ordemNac?.id]?.progresso || ordemNac?.progresso || e.nacional.atualizado_em || ''}</div>
                      </td>
                      <td>
                        {ordemSp || e.sp.status ? <SeloStatus prov={e.sp} /> : <span className="fz-sub">{e.sao_paulo ? 'CCM cadastrado' : '—'}</span>}
                      </td>
                      <td className="text-end fz-num">{fmtNum(e.documentos)}</td>
                      <td className="text-end" style={{ whiteSpace: 'nowrap' }}>
                        {pendente && (
                          <button className="btn btn-sm btn-outline-success me-1" onClick={() => processar(pendente.id)} disabled={!!processando[pendente.id]} title="Processar agora">
                            <i className={`fas ${processando[pendente.id] ? 'fa-spinner fa-spin' : 'fa-play'}`}></i>
                          </button>
                        )}
                        {e.habilitada ? (
                          <>
                            <button className="btn btn-sm btn-outline-primary me-1" onClick={() => abrirNovaOrdem({ modo: 'empresa', empresas: [e] })}><i className="fas fa-download me-1"></i>Nova ordem</button>
                            <button className="btn btn-sm btn-outline-secondary" title="Desabilitar (mantém NSU e documentos)" onClick={() => setDesabilitar(e)}><i className="fas fa-power-off"></i></button>
                          </>
                        ) : (
                          <button className="btn btn-sm btn-outline-secondary" onClick={() => patch(e.cod, { habilitada: true }, () => {})} disabled={salvando}><i className="fas fa-rotate-left me-1"></i>Reabilitar</button>
                        )}
                      </td>
                    </tr>
                  );
                })}
                {lista.length === 0 && <tr><td colSpan={7} className="text-center text-muted py-4">Nenhuma empresa com esses filtros.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <section className="po-card po-enter mt-3">
        <header className="po-card-head">
          <h3><i className="fas fa-list-check" aria-hidden="true"></i>Ordens recentes</h3>
          <button type="button" className="gr-btn" onClick={carregarOrdens}><i className="fas fa-rotate"></i>Atualizar</button>
        </header>
        <div className="po-card-body">
          <div className="po-scroll" style={{ maxHeight: 360 }}>
            <table className="table table-hover po-table align-middle mb-0">
              <thead><tr><th>Criada</th><th>Empresa</th><th>Provedor</th><th>Situação</th><th>Resultado</th><th></th></tr></thead>
              <tbody>
                {ordens.map((o) => {
                  const st = processando[o.id] ? 'Processando' : o.status_txt;
                  return (
                    <tr key={o.id}>
                      <td className="small" style={{ whiteSpace: 'nowrap' }}>{o.criado_em}<div className="fz-sub">{o.grupo ? `grupo ${o.grupo}` : o.solicitado_por || ''}</div></td>
                      <td><div className="fw-semibold small">{o.cliente || `Empresa ${o.empresa_cod}`}</div><div className="fz-sub fz-mono">{o.empresa_cod}</div></td>
                      <td className="small">{PROVEDORES[o.provedor] || o.provedor}{o.provedor === 'sao_paulo' && o.parametros?.inicio && <div className="fz-sub">{o.parametros.inicio.split('-').reverse().join('/')} a {o.parametros.fim.split('-').reverse().join('/')}</div>}</td>
                      <td><SeloStatus prov={{ status: st }} /></td>
                      <td className="small" style={{ maxWidth: 420 }}>
                        <div style={{ overflowWrap: 'anywhere' }}>{processando[o.id]?.progresso || o.progresso || o.mensagem || '—'}</div>
                        <ResumoResultado r={processando[o.id]?.resultado || o.resultado} />
                      </td>
                      <td className="text-end">
                        {o.status === 'pendente' && (
                          <button className="btn btn-sm btn-outline-success" onClick={() => processar(o.id)} disabled={!!processando[o.id]}>
                            <i className={`fas ${processando[o.id] ? 'fa-spinner fa-spin' : 'fa-play'} me-1`}></i>Processar agora
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
                {ordens.length === 0 && <tr><td colSpan={6} className="text-center text-muted py-4">Nenhuma ordem ainda.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {habilitar && (
        <Modal titulo="Habilitar empresa no Monitor" icone="fa-plus" onFechar={() => setHabilitar(null)} travado={salvando}
          rodape={<>
            <button className="btn btn-secondary me-auto" onClick={() => setHabilitar(null)} disabled={salvando}><i className="fas fa-times me-2"></i>Cancelar</button>
            <button className="btn btn-primary" onClick={salvarHabilitar} disabled={salvando || !habilitar.cod || !/^\d*$/.test(habilitar.nsu)}>
              <i className={`fas ${salvando ? 'fa-spinner fa-spin' : 'fa-check'} me-2`}></i>Habilitar
            </button>
          </>}>
          <label className="form-label small fw-bold mb-1">Empresa</label>
          <input className="form-control mb-2" placeholder="Filtrar por nome ou código" value={habilitar.busca} onChange={(ev) => filtrarHabilitar(ev.target.value)} />
          <select className="form-select mb-3" size={8} value={habilitar.cod} onChange={(ev) => setHabilitar((s) => ({ ...s, cod: ev.target.value }))}>
            {disponiveis.map((e) => <option key={e.cod} value={e.cod}>{e.cod} · {e.cliente}{e.ativa ? '' : ' (inativa)'}</option>)}
          </select>
          <label className="form-label small fw-bold mb-1">NSU inicial</label>
          <input className="form-control fz-mono" inputMode="numeric" value={habilitar.nsu} onChange={(ev) => setHabilitar((s) => ({ ...s, nsu: ev.target.value.replace(/\D/g, '') }))} />
          <div className="fz-sub mt-1">0 = baixa tudo o que o ADN tiver para o CNPJ. Para seguir de onde o ZADRA parou, informe o último NSU dele.</div>
        </Modal>
      )}

      {editNsu && (
        <Modal titulo="Editar último NSU" subtitulo={`${editNsu.empresa.cod} · ${editNsu.empresa.cliente}`} icone="fa-pen" onFechar={() => setEditNsu(null)} travado={salvando}
          rodape={<>
            <button className="btn btn-secondary me-auto" onClick={() => setEditNsu(null)} disabled={salvando}><i className="fas fa-times me-2"></i>Cancelar</button>
            <button className="btn btn-primary" onClick={() => patch(editNsu.empresa.cod, { ultimo_nsu: Number(editNsu.nsu) }, () => setEditNsu(null))} disabled={salvando || editNsu.nsu === ''}>
              <i className={`fas ${salvando ? 'fa-spinner fa-spin' : 'fa-floppy-disk'} me-2`}></i>Salvar
            </button>
          </>}>
          <input className="form-control fz-mono" inputMode="numeric" value={editNsu.nsu} onChange={(ev) => setEditNsu((s) => ({ ...s, nsu: ev.target.value.replace(/\D/g, '') }))} />
          <div className="alert alert-warning py-2 small mt-3 mb-0"><i className="fas fa-triangle-exclamation me-2"></i>
            Voltar o NSU faz o ADN devolver notas já baixadas (elas são reconhecidas e não duplicam). Avançar pula notas que ainda não foram baixadas.
          </div>
        </Modal>
      )}

      <ConfirmarModal aberto={!!desabilitar} titulo="Desabilitar no Monitor" perigo rotuloConfirmar="Desabilitar" processando={salvando}
        texto={desabilitar && <>A empresa <b>{desabilitar.cliente}</b> deixa de receber ordens (inclusive dos grupos). O último NSU e os XMLs já baixados ficam guardados.</>}
        onCancelar={() => setDesabilitar(null)} onConfirmar={() => patch(desabilitar.cod, { habilitada: false }, () => setDesabilitar(null))} />
    </>
  );
}

// ---------------------------------------------------------------------------
// Aba: Grupos
// ---------------------------------------------------------------------------
function ListaVinculo({ titulo, itens, onMover, icone }) {
  const [busca, setBusca] = useState('');
  const vis = itens.filter((e) => !busca || semAcento(e.cliente).includes(semAcento(busca)) || String(e.cod) === busca.trim());
  return (
    <div className="col">
      <div className="fw-bold small mb-1">{titulo} <span className="badge bg-light text-dark">{itens.length}</span></div>
      <input className="form-control form-control-sm mb-2" placeholder="Filtrar" value={busca} onChange={(e) => setBusca(e.target.value)} />
      <div className="border rounded" style={{ height: 320, overflowY: 'auto' }}>
        {vis.map((e) => (
          <button key={e.cod} type="button" className="d-flex w-100 align-items-center justify-content-between border-0 border-bottom bg-white px-2 py-2 text-start small" onClick={() => onMover(e)}>
            <span><span className="fz-mono text-muted me-2">{e.cod}</span>{e.cliente}</span><i className={`fas ${icone} text-primary`}></i>
          </button>
        ))}
        {vis.length === 0 && <div className="text-muted small p-3 text-center">Nenhuma empresa.</div>}
      </div>
    </div>
  );
}

function AbaGrupos({ grupos, setGrupos, setErro, setMsg, abrirNovaOrdem, recarregarEmpresas }) {
  const [form, setForm] = useState(null);       // { id?, nome, periodicidade, ativo }
  const [vinculo, setVinculo] = useState(null); // { grupo, no_grupo, elegiveis }
  const [excluir, setExcluir] = useState(null);
  const [salvando, setSalvando] = useState(false);

  const salvar = () => {
    setSalvando(true);
    const corpo = { nome: form.nome, periodicidade: form.periodicidade, ativo: form.ativo };
    const req = form.id ? axios.put(`${URL_MON}/grupos/${form.id}/`, corpo, { headers: headers() }) : axios.post(`${URL_MON}/grupos/`, corpo, { headers: headers() });
    req.then((r) => { setGrupos(r.data.grupos); setMsg(r.data.mensagem); setForm(null); })
      .catch((e) => setErro(msgErro(e, 'Não foi possível salvar o grupo')))
      .finally(() => setSalvando(false));
  };

  const abrirVinculo = (g) => {
    axios.get(`${URL_MON}/grupos/${g.id}/empresas/`, { params: { _: Date.now() }, headers: headers() })
      .then((r) => setVinculo({ grupo: g, no_grupo: r.data.no_grupo, elegiveis: r.data.elegiveis }))
      .catch((e) => setErro(msgErro(e, 'Não foi possível carregar os vínculos')));
  };

  const mover = (e, paraGrupo) => setVinculo((v) => {
    const ordenar = (l) => [...l].sort((a, b) => (a.cliente || '').localeCompare(b.cliente || ''));
    return paraGrupo
      ? { ...v, no_grupo: ordenar([...v.no_grupo, e]), elegiveis: v.elegiveis.filter((x) => x.cod !== e.cod) }
      : { ...v, elegiveis: ordenar([...v.elegiveis, e]), no_grupo: v.no_grupo.filter((x) => x.cod !== e.cod) };
  });

  const salvarVinculo = () => {
    setSalvando(true);
    axios.put(`${URL_MON}/grupos/${vinculo.grupo.id}/empresas/`, { empresas: vinculo.no_grupo.map((e) => e.cod) }, { headers: headers() })
      .then((r) => { setGrupos(r.data.grupos); setMsg(r.data.mensagem); setVinculo(null); recarregarEmpresas(); })
      .catch((e) => setErro(msgErro(e, 'Não foi possível salvar os vínculos')))
      .finally(() => setSalvando(false));
  };

  const confirmarExclusao = () => {
    setSalvando(true);
    axios.delete(`${URL_MON}/grupos/${excluir.id}/`, { headers: headers() })
      .then((r) => { setGrupos(r.data.grupos); setMsg(r.data.mensagem); setExcluir(null); recarregarEmpresas(); })
      .catch((e) => setErro(msgErro(e, 'Não foi possível excluir o grupo')))
      .finally(() => setSalvando(false));
  };

  return (
    <>
      <div className="alert alert-light border small py-2">
        <i className="fas fa-circle-info me-2 text-primary"></i>
        Grupos <b>ativos</b> geram ordens sozinhos quando a periodicidade vence (contada da última execução). Só entram empresas habilitadas, com certificado em dia e sem ordem em aberto.
      </div>
      <div className="fz-filtros">
        <button type="button" className="gr-btn" onClick={() => setForm({ nome: '', periodicidade: 'Semanal', ativo: true })}><i className="fas fa-plus"></i>Novo grupo</button>
      </div>
      <section className="po-card po-enter">
        <header className="po-card-head">
          <h3><i className="fas fa-layer-group" aria-hidden="true"></i>Grupos</h3>
          <span className="badge bg-light text-dark">{grupos.length}</span>
        </header>
        <div className="po-card-body">
          <table className="table table-hover po-table align-middle mb-0">
            <thead><tr><th>Grupo</th><th>Periodicidade</th><th className="text-end">Empresas</th><th>Última execução</th><th>Próxima</th><th></th></tr></thead>
            <tbody>
              {grupos.map((g) => (
                <tr key={g.id}>
                  <td className="fw-semibold">{g.nome}{!g.ativo && <span className="fz-selo neutro ms-2">inativo</span>}</td>
                  <td>{(PERIODICIDADES.find((p) => p[0] === g.periodicidade) || [null, g.periodicidade || '—'])[1]}</td>
                  <td className="text-end fz-num">{g.total_empresas}</td>
                  <td className="small">{g.ultima_execucao || '—'}</td>
                  <td className="small">{g.ativo ? g.proxima_execucao || '—' : '—'}</td>
                  <td className="text-end" style={{ whiteSpace: 'nowrap' }}>
                    <button className="btn btn-sm btn-outline-primary me-1" onClick={() => abrirVinculo(g)}><i className="fas fa-right-left me-1"></i>Empresas</button>
                    <button className="btn btn-sm btn-outline-primary me-1" onClick={() => abrirNovaOrdem({ modo: 'grupo', grupo_id: g.id })} disabled={!g.total_empresas}><i className="fas fa-download me-1"></i>Gerar ordens</button>
                    <button className="btn btn-sm btn-outline-secondary me-1" title="Editar" onClick={() => setForm({ id: g.id, nome: g.nome, periodicidade: g.periodicidade || 'Semanal', ativo: g.ativo })}><i className="fas fa-pen"></i></button>
                    <button className="btn btn-sm btn-outline-danger" title="Excluir" onClick={() => setExcluir(g)}><i className="fas fa-trash"></i></button>
                  </td>
                </tr>
              ))}
              {grupos.length === 0 && <tr><td colSpan={6} className="text-center text-muted py-4">Nenhum grupo cadastrado.</td></tr>}
            </tbody>
          </table>
        </div>
      </section>

      {form && (
        <Modal titulo={form.id ? 'Editar grupo' : 'Novo grupo'} icone="fa-layer-group" onFechar={() => setForm(null)} travado={salvando}
          rodape={<>
            <button className="btn btn-secondary me-auto" onClick={() => setForm(null)} disabled={salvando}><i className="fas fa-times me-2"></i>Cancelar</button>
            <button className="btn btn-primary" onClick={salvar} disabled={salvando || !form.nome.trim()}><i className={`fas ${salvando ? 'fa-spinner fa-spin' : 'fa-floppy-disk'} me-2`}></i>Salvar</button>
          </>}>
          <label className="form-label small fw-bold mb-1">Nome</label>
          <input className="form-control mb-3" maxLength={80} value={form.nome} onChange={(e) => setForm((f) => ({ ...f, nome: e.target.value }))} />
          <label className="form-label small fw-bold mb-1">Periodicidade</label>
          <select className="form-select mb-3" value={form.periodicidade} onChange={(e) => setForm((f) => ({ ...f, periodicidade: e.target.value }))}>
            {PERIODICIDADES.map(([v, t]) => <option key={v} value={v}>{t}</option>)}
          </select>
          <label className="po-toggle"><input type="checkbox" checked={form.ativo} onChange={(e) => setForm((f) => ({ ...f, ativo: e.target.checked }))} />Gerar ordens automaticamente</label>
        </Modal>
      )}

      {vinculo && (
        <Modal titulo="Empresas do grupo" subtitulo={vinculo.grupo.nome} icone="fa-right-left" largo onFechar={() => setVinculo(null)} travado={salvando}
          rodape={<>
            <button className="btn btn-secondary me-auto" onClick={() => setVinculo(null)} disabled={salvando}><i className="fas fa-times me-2"></i>Cancelar</button>
            <button className="btn btn-primary" onClick={salvarVinculo} disabled={salvando}><i className={`fas ${salvando ? 'fa-spinner fa-spin' : 'fa-floppy-disk'} me-2`}></i>Salvar vínculos</button>
          </>}>
          <div className="row g-3">
            <ListaVinculo titulo="Habilitadas fora do grupo" itens={vinculo.elegiveis} icone="fa-arrow-right" onMover={(e) => mover(e, true)} />
            <ListaVinculo titulo="No grupo" itens={vinculo.no_grupo} icone="fa-arrow-left" onMover={(e) => mover(e, false)} />
          </div>
          <div className="fz-sub mt-2">Clique numa empresa para passá-la para o outro lado. Salvar substitui todos os vínculos do grupo.</div>
        </Modal>
      )}

      <ConfirmarModal aberto={!!excluir} titulo="Excluir grupo" perigo rotuloConfirmar="Excluir" processando={salvando}
        texto={excluir && <>Excluir o grupo <b>{excluir.nome}</b> e os vínculos dele? As empresas continuam no Monitor e as ordens antigas ficam no histórico.</>}
        onCancelar={() => setExcluir(null)} onConfirmar={confirmarExclusao} />
    </>
  );
}

// ---------------------------------------------------------------------------
// Aba: Documentos
// ---------------------------------------------------------------------------
function AbaDocumentos({ todasEmpresas, setErro }) {
  const [cod, setCod] = useState('');
  const [busca, setBusca] = useState('');
  const [provedor, setProvedor] = useState('');
  const [resumo, setResumo] = useState(null);
  const [comp, setComp] = useState(null);
  const [tipo, setTipo] = useState('');
  const [docs, setDocs] = useState([]);
  const [carregando, setCarregando] = useState(false);
  const [baixando, setBaixando] = useState('');
  const [sel, setSel] = useState(() => new Set());   // ids marcados para o ZIP em lote
  const [selComp, setSelComp] = useState(() => new Set());   // competências marcadas para o ZIP

  const opcoes = useMemo(() => {
    const termo = semAcento(busca);
    const dig = busca.replace(/\D/g, '');
    return todasEmpresas.filter((e) => !termo || semAcento(e.cliente).includes(termo) || String(e.cod) === busca.trim()
      || (dig.length >= 4 && e.cnpj.includes(dig)));
  }, [todasEmpresas, busca]);

  const carregar = useCallback((competencia) => {
    if (!cod) return;
    setCarregando(true);
    axios.get(`${URL_MON}/documentos/`, { params: { empresa: cod, provedor: provedor || undefined, competencia: competencia || undefined, _: Date.now() }, headers: headers() })
      .then((r) => { setResumo(r.data.resumo); setDocs(r.data.documentos); })
      .catch((e) => setErro(msgErro(e, 'Não foi possível carregar os documentos')))
      .finally(() => setCarregando(false));
  }, [cod, provedor, setErro]);

  useEffect(() => { setComp(null); setDocs([]); setResumo(null); carregar(null); }, [carregar]);
  useEffect(() => { setSel(new Set()); }, [cod, comp, tipo, provedor]);
  useEffect(() => { setSelComp(new Set()); }, [cod, provedor]);

  const abrirComp = (c) => { setComp(c); setTipo(''); carregar(c); };

  const zip = (c) => {
    setBaixando(`zip-${c}`);
    baixarArquivo(`${URL_MON}/documentos/zip/`, { empresa: cod, competencia: c, provedor: provedor || undefined }, `notas-${c}.zip`)
      .catch(async (e) => setErro(await erroDeBlob(e, 'Não foi possível baixar o ZIP')))
      .finally(() => setBaixando(''));
  };

  const xml = (d) => {
    setBaixando(`xml-${d.id}`);
    baixarArquivo(`${URL_MON}/documentos/${d.id}/xml/`, {}, d.nome_arquivo || 'nota.xml')
      .catch(async (e) => setErro(await erroDeBlob(e, 'Não foi possível baixar o XML')))
      .finally(() => setBaixando(''));
  };

  const docsVis = docs.filter((d) => !tipo || d.tipo === tipo);
  const todosMarcados = docsVis.length > 0 && docsVis.every((d) => sel.has(d.id));
  const marcar = (id) => setSel((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const marcarTodos = () => setSel(todosMarcados ? new Set() : new Set(docsVis.map((d) => d.id)));
  const comps = (resumo || []).map((r) => r.competencia);
  const todasComp = comps.length > 0 && comps.every((c) => selComp.has(c));
  const marcarComp = (c) => setSelComp((s) => { const n = new Set(s); if (n.has(c)) n.delete(c); else n.add(c); return n; });
  const marcarTodasComp = () => setSelComp(todasComp ? new Set() : new Set(comps));
  const totalSelComp = (resumo || []).filter((r) => selComp.has(r.competencia)).reduce((t, r) => t + (r.total || 0), 0);
  const zipCompetencias = () => {
    setBaixando('zip-comps');
    baixarArquivoPost(`${URL_MON}/documentos/zip/`, { empresa: cod, competencias: [...selComp], provedor: provedor || undefined }, 'notas-competencias.zip')
      .catch(async (e) => setErro(await erroDeBlob(e, 'Não foi possível baixar as competências')))
      .finally(() => setBaixando(''));
  };
  const zipSelecionados = () => {
    setBaixando('zip-sel');
    baixarArquivoPost(`${URL_MON}/documentos/zip/`, { empresa: cod, ids: [...sel] }, `notas-selecao-${sel.size}.zip`)
      .catch(async (e) => setErro(await erroDeBlob(e, 'Não foi possível baixar os selecionados')))
      .finally(() => setBaixando(''));
  };

  return (
    <>
      <div className="fz-filtros">
        <input className="po-busca" style={{ flex: '0 1 220px' }} placeholder="Código, nome ou CNPJ" value={busca}
          onChange={(e) => {
            // código exato ou um único resultado pelo nome já seleciona a empresa
            const valor = e.target.value;
            setBusca(valor);
            const termo = semAcento(valor);
            const digitos = valor.replace(/\D/g, '');
            const porCodigo = todasEmpresas.find((x) => String(x.cod) === valor.trim())
              || (digitos.length === 14 ? todasEmpresas.find((x) => x.cnpj === digitos) : null);
            const porNome = termo ? todasEmpresas.filter((x) => semAcento(x.cliente).includes(termo)) : [];
            const achada = porCodigo || (porNome.length === 1 ? porNome[0] : null);
            if (achada) setCod(String(achada.cod));
          }}
          onKeyDown={(e) => { if (e.key === 'Enter' && opcoes.length) setCod(String(opcoes[0].cod)); }} />
        <select className="fz-sel" style={{ flex: '1 1 320px' }} value={cod} onChange={(e) => setCod(e.target.value)}>
          <option value="">Selecione a empresa…</option>
          {opcoes.map((e) => <option key={e.cod} value={e.cod}>{e.cod} · {e.cliente}</option>)}
        </select>
        <select className="fz-sel" value={provedor} onChange={(e) => setProvedor(e.target.value)}>
          <option value="">Todos os provedores</option>
          <option value="nacional">Nacional (ADN)</option>
          <option value="sao_paulo">São Paulo</option>
        </select>
      </div>

      {!cod && <div className="text-center text-muted py-5">Escolha uma empresa para ver os XMLs baixados por competência.</div>}

      {cod && (
        <div className="row g-3">
          <div className="col-lg-5">
            <section className="po-card po-enter">
              <header className="po-card-head">
                <h3><i className="fas fa-calendar" aria-hidden="true"></i>Competências</h3>{carregando && <i className="fas fa-spinner fa-spin text-muted"></i>}
                <button className="btn btn-sm btn-primary ms-auto" onClick={zipCompetencias} disabled={!selComp.size || !!baixando}
                  title={selComp.size ? `${totalSelComp} documento(s) num ZIP, uma pasta por competência` : 'Marque as competências'}>
                  <i className={`fas ${baixando === 'zip-comps' ? 'fa-spinner fa-spin' : 'fa-file-zipper'} me-1`}></i>Baixar competências{selComp.size ? ` (${selComp.size})` : ''}
                </button>
              </header>
              <div className="po-card-body">
                <div className="po-scroll" style={{ maxHeight: 'calc(100vh - 260px)', minHeight: 480 }}>
                  <table className="table table-hover po-table align-middle mb-0">
                    <thead><tr><th style={{ width: 32 }}><input type="checkbox" className="form-check-input" checked={todasComp} onChange={marcarTodasComp} disabled={!comps.length} title="Marcar todas" /></th><th>Competência</th><th className="text-end">Emitidas</th><th className="text-end">Recebidas</th><th className="text-end">Eventos</th><th></th></tr></thead>
                    <tbody>
                      {(resumo || []).map((r) => (
                        <tr key={r.competencia} style={comp === r.competencia || selComp.has(r.competencia) ? { background: 'rgba(0,181,204,.08)' } : undefined}>
                          <td><input type="checkbox" className="form-check-input" checked={selComp.has(r.competencia)} onChange={() => marcarComp(r.competencia)} aria-label="Selecionar competência" /></td>
                          <td><button className="btn btn-link p-0 fw-semibold" onClick={() => abrirComp(r.competencia)}>{fmtComp(r.competencia)}</button></td>
                          <td className="text-end fz-num">{r.emitidas}<div className="fz-sub">{fmtValor(r.valor_emitidas)}</div></td>
                          <td className="text-end fz-num">{r.recebidas}<div className="fz-sub">{fmtValor(r.valor_recebidas)}</div></td>
                          <td className="text-end fz-num">{r.eventos}</td>
                          <td className="text-end">
                            {r.competencia !== 'SemData' && (
                              <button className="btn btn-sm btn-outline-primary" title="Baixar ZIP da competência" onClick={() => zip(r.competencia)} disabled={!!baixando}>
                                <i className={`fas ${baixando === `zip-${r.competencia}` ? 'fa-spinner fa-spin' : 'fa-file-zipper'}`}></i>
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                      {resumo && resumo.length === 0 && <tr><td colSpan={6} className="text-center text-muted py-4">Nenhum XML baixado para esta empresa.</td></tr>}
                    </tbody>
                  </table>
                </div>
              </div>
            </section>
          </div>
          <div className="col-lg-7">
            <section className="po-card po-enter">
              <header className="po-card-head">
                <h3><i className="fas fa-file-code" aria-hidden="true"></i>{comp ? `Documentos de ${fmtComp(comp)}` : 'Documentos'}</h3>
                {comp && (
                  <div className="d-flex align-items-center gap-2 ms-auto">
                    <button className="btn btn-sm btn-primary" onClick={zipSelecionados} disabled={!sel.size || !!baixando} title="Baixa um ZIP só com as notas marcadas">
                      <i className={`fas ${baixando === 'zip-sel' ? 'fa-spinner fa-spin' : 'fa-file-zipper'} me-1`}></i>Baixar selecionados{sel.size ? ` (${sel.size})` : ''}
                    </button>
                    <select className="fz-sel" value={tipo} onChange={(e) => setTipo(e.target.value)}>
                      <option value="">Todos os tipos</option>
                      {Object.entries(TIPOS).map(([k, t]) => <option key={k} value={k}>{t}s</option>)}
                    </select>
                  </div>
                )}
              </header>
              <div className="po-card-body">
                {!comp ? <div className="d-flex align-items-center justify-content-center text-muted" style={{ minHeight: 480 }}>Clique numa competência.</div> : (
                  <div className="po-scroll" style={{ maxHeight: 'calc(100vh - 260px)', minHeight: 480 }}>
                    <table className="table table-hover po-table align-middle mb-0">
                      <thead><tr><th style={{ width: 32 }}><input type="checkbox" className="form-check-input" checked={todosMarcados} onChange={marcarTodos} disabled={!docsVis.length} title="Marcar todos os visíveis" /></th><th>Tipo</th><th>Número</th><th>Emissão</th><th>Prestador / Tomador</th><th className="text-end">Valor</th><th></th></tr></thead>
                      <tbody>
                        {docsVis.map((d) => (
                          <tr key={d.id} style={sel.has(d.id) ? { background: '#e6f8fb' } : undefined}>
                            <td><input type="checkbox" className="form-check-input" checked={sel.has(d.id)} onChange={() => marcar(d.id)} aria-label="Selecionar nota" /></td>
                            <td className="small">{TIPOS[d.tipo] || d.tipo}<div className="fz-sub">{PROVEDORES[d.provedor] || d.provedor}{d.nsu ? ` · NSU ${d.nsu}` : ''}</div></td>
                            <td className="fz-mono small">{d.numero || '—'}</td>
                            <td className="small">{d.data_emissao || '—'}</td>
                            <td className="fz-mono small">{fmtCnpj(d.prestador_cnpj)}<div className="fz-sub">{fmtCnpj(d.tomador_cnpj)}</div></td>
                            <td className="text-end fz-num small">{fmtValor(d.valor)}</td>
                            <td className="text-end">
                              <button className="btn btn-sm btn-outline-primary" title="Baixar XML" onClick={() => xml(d)} disabled={!!baixando}>
                                <i className={`fas ${baixando === `xml-${d.id}` ? 'fa-spinner fa-spin' : 'fa-download'}`}></i>
                              </button>
                            </td>
                          </tr>
                        ))}
                        {docsVis.length === 0 && !carregando && <tr><td colSpan={7} className="text-center text-muted py-4">Nenhum documento.</td></tr>}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </section>
          </div>
        </div>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Aba: Notas retroativas
// ---------------------------------------------------------------------------
function AbaRetroativas({ todasEmpresas, setErro }) {
  const [filtro, setFiltro] = useState({ empresa: '', inicio: '', fim: '' });
  const [busca, setBusca] = useState('');
  const [itens, setItens] = useState(null);
  const [carregando, setCarregando] = useState(false);

  const carregar = useCallback(() => {
    setCarregando(true);
    axios.get(`${URL_MON}/retroativas/`, { params: { empresa: filtro.empresa || undefined, inicio: filtro.inicio || undefined, fim: filtro.fim || undefined, _: Date.now() }, headers: headers() })
      .then((r) => setItens(r.data.retroativas))
      .catch((e) => setErro(msgErro(e, 'Não foi possível carregar as notas retroativas')))
      .finally(() => setCarregando(false));
  }, [filtro, setErro]);
  useEffect(() => { carregar(); }, [carregar]);

  // código exato, CNPJ completo ou um único nome seleciona a empresa (consulta no servidor);
  // o texto também filtra as linhas por empresa, prestador e tomador (nome ou CNPJ)
  const buscar = (valor) => {
    setBusca(valor);
    const t = valor.trim();
    if (!t) { setFiltro((f) => ({ ...f, empresa: '' })); return; }
    const dig = t.replace(/\D/g, '');
    const termo = semAcento(t);
    const achada = todasEmpresas.find((x) => String(x.cod) === t)
      || (dig.length === 14 ? todasEmpresas.find((x) => (x.cnpj || '') === dig) : null)
      || (() => { const n = todasEmpresas.filter((x) => semAcento(x.cliente).includes(termo)); return n.length === 1 ? n[0] : null; })();
    // sem empresa única, solta a escolhida antes (senão o filtro antigo ficava preso)
    setFiltro((f) => ({ ...f, empresa: achada ? String(achada.cod) : '' }));
  };
  const visiveis = useMemo(() => {
    const t = busca.trim();
    if (!t || !itens) return itens;
    const termo = semAcento(t);
    const dig = t.replace(/\D/g, '');
    const cnpjEmpresa = Object.fromEntries(todasEmpresas.map((x) => [String(x.cod), x.cnpj || '']));
    return itens.filter((r) => String(r.empresa_cod) === t
      || [r.cliente, r.prestador_nome, r.tomador_nome].some((n) => semAcento(n).includes(termo))
      || (dig.length >= 4 && [cnpjEmpresa[String(r.empresa_cod)], r.prestador_cnpj, r.tomador_cnpj].some((c) => (c || '').includes(dig))));
  }, [itens, busca, todasEmpresas]);

  return (
    <>
      <div className="alert alert-light border small py-2">
        <i className="fas fa-circle-info me-2 text-primary"></i>
        Nota <b>retroativa</b>: emitida a partir do dia 10 com competência de mês anterior — sinal de que deveria ter saído antes do fechamento. Detectada na hora em que o XML é baixado pela primeira vez.
        Sem filtro, mostra as detectadas nos últimos 90 dias.
      </div>
      <div className="fz-filtros">
        <input className="po-busca" style={{ flex: '0 1 240px' }} placeholder="Código, nome ou CNPJ" value={busca}
          onChange={(e) => buscar(e.target.value)} />
        <select className="fz-sel" style={{ flex: '1 1 320px' }} value={filtro.empresa} onChange={(e) => setFiltro((f) => ({ ...f, empresa: e.target.value }))}>
          <option value="">Todas as empresas</option>
          {todasEmpresas.map((e) => <option key={e.cod} value={e.cod}>{e.cod} · {e.cliente}</option>)}
        </select>
        <label className="small fw-bold">Emissão de</label>
        <input type="date" className="fz-sel" value={filtro.inicio} onChange={(e) => setFiltro((f) => ({ ...f, inicio: e.target.value }))} />
        <label className="small fw-bold">até</label>
        <input type="date" className="fz-sel" value={filtro.fim} onChange={(e) => setFiltro((f) => ({ ...f, fim: e.target.value }))} />
        {carregando && <i className="fas fa-spinner fa-spin text-muted"></i>}
      </div>
      <section className="po-card po-enter">
        <header className="po-card-head">
          <h3><i className="fas fa-clock-rotate-left" aria-hidden="true"></i>Notas retroativas</h3>
          <span className="badge bg-light text-dark">{(visiveis || []).length}</span>
        </header>
        <div className="po-card-body">
          <div className="po-scroll" style={{ maxHeight: 'calc(100vh - 300px)', minHeight: 420 }}>
            <table className="table table-hover po-table align-middle mb-0">
              <thead><tr><th>Empresa</th><th>Nota</th><th>Emissão</th><th>Competência</th><th className="text-end">Valor</th><th>Prestador</th><th>Tomador</th><th>Detectada</th></tr></thead>
              <tbody>
                {(visiveis || []).map((r) => (
                  <tr key={r.id}>
                    <td><div className="fw-semibold small">{r.cliente || `Empresa ${r.empresa_cod}`}</div><div className="fz-sub fz-mono">{r.empresa_cod}</div></td>
                    <td className="small"><span className="fz-mono">{r.numero || '—'}</span><div><span className={`fz-selo ${r.emitida ? 'alerta' : 'neutro'}`}>{r.emitida ? 'emitida' : 'recebida'}</span></div></td>
                    <td className="small">{r.data_emissao}</td>
                    <td className="small">{fmtComp(r.competencia)}</td>
                    <td className="text-end fz-num small">{fmtValor(r.valor)}</td>
                    <td className="small">{r.prestador_nome || '—'}<div className="fz-sub fz-mono">{fmtCnpj(r.prestador_cnpj)}</div></td>
                    <td className="small">{r.tomador_nome || '—'}<div className="fz-sub fz-mono">{fmtCnpj(r.tomador_cnpj)}</div></td>
                    <td className="small">{r.detectado_em}</td>
                  </tr>
                ))}
                {visiveis && visiveis.length === 0 && <tr><td colSpan={8} className="text-center text-muted py-4">Nenhuma nota retroativa nesse filtro.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      </section>
    </>
  );
}

// ---------------------------------------------------------------------------
// Página
// ---------------------------------------------------------------------------
export default function FiscalMonitor() {
  const [aba, setAba] = useState(() => {
    try { return localStorage.getItem('fiscalMonitorAba') || 'empresas'; } catch { return 'empresas'; }
  });
  const [dados, setDados] = useState(null);
  const [grupos, setGrupos] = useState([]);
  const [ordens, setOrdens] = useState([]);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState('');
  const [msg, setMsg] = useState('');
  const [novaOrdem, setNovaOrdem] = useState(null); // { modo, empresas?, grupo_id, incluir_sp, sp_inicio, sp_fim, max_lotes, resultado }
  const [criando, setCriando] = useState(false);
  const [processando, setProcessando] = useState({}); // { ordemId: { progresso, resultado } }
  const timers = useRef({});

  const carregarEmpresas = useCallback(() => {
    setCarregando(true);
    return axios.get(`${URL_MON}/empresas/`, { params: { _: Date.now() }, headers: headers() })
      .then((r) => { setDados(r.data); setErro(''); })
      .catch((e) => setErro(msgErro(e, 'Não foi possível carregar o Monitor')))
      .finally(() => setCarregando(false));
  }, []);
  const carregarGrupos = useCallback(() => axios.get(`${URL_MON}/grupos/`, { params: { _: Date.now() }, headers: headers() })
    .then((r) => setGrupos(r.data.grupos))
    .catch((e) => setErro(msgErro(e, 'Não foi possível carregar os grupos'))), []);
  const carregarOrdens = useCallback(() => axios.get(`${URL_MON}/ordens/`, { params: { limite: 60, _: Date.now() }, headers: headers() })
    .then((r) => setOrdens(r.data.ordens))
    .catch((e) => setErro(msgErro(e, 'Não foi possível carregar as ordens'))), []);

  const recarregarTudo = useCallback(() => { carregarEmpresas(); carregarGrupos(); carregarOrdens(); }, [carregarEmpresas, carregarGrupos, carregarOrdens]);
  useEffect(() => { recarregarTudo(); }, [recarregarTudo]);
  useEffect(() => { try { localStorage.setItem('fiscalMonitorAba', aba); } catch { /* sem armazenamento local */ } }, [aba]);
  // marca "tela aberta" a cada montagem: o StrictMode do React monta/desmonta/monta em
  // desenvolvimento, e só zerar no desmontar deixava a tela "fechada" para sempre — a ordem
  // concluía, mas o "rodando" nunca saía (16/10/2026)
  const montado = useRef(true);
  useEffect(() => {
    montado.current = true;
    const ativos = timers.current; // mesmo objeto o tempo todo (as chaves é que mudam)
    return () => { montado.current = false; Object.values(ativos).forEach(clearInterval); };
  }, []);

  const todasEmpresas = useMemo(() => {
    const m = new Map();
    // cnpj só com dígitos: a busca por CNPJ (Documentos e Retroativas) compara número com número
    const item = (e) => ({ cod: e.cod, cliente: e.cliente, cnpj: (e.cnpj || '').replace(/\D/g, '') });
    (dados?.empresas || []).forEach((e) => m.set(e.cod, item(e)));
    (dados?.disponiveis || []).forEach((e) => { if (!m.has(e.cod)) m.set(e.cod, item(e)); });
    return [...m.values()].sort((a, b) => (a.cliente || '').localeCompare(b.cliente || ''));
  }, [dados]);

  // Cada "Processar agora" roda até ~4 min no servidor; se o tempo acaba, a ordem volta para
  // Pendente com o NSU salvo. Enquanto esta tela estiver aberta, emenda a próxima rodada sozinha
  // até concluir (ou dar erro) — antes era preciso clicar de novo a cada ~12 lotes.
  const processar = useCallback((ordemId) => {
    let rodada = 1;
    let acabou = false;
    const avisar = (o) => {
      if (o?.status === 'concluida') setMsg(`${o.cliente || o.empresa_cod}: ${o.mensagem}`);
      else if (o?.status === 'erro') setErro(`${o.cliente || o.empresa_cod}: ${o.mensagem}`);
      else if (o?.status === 'pendente') setMsg(`${o.cliente || o.empresa_cod}: ${o.mensagem || 'continua na fila'}`);
    };
    // o "rodando" sempre sai quando a ordem termina (uma vez só, venha o fim pela resposta
    // do processar ou pelo acompanhamento abaixo)
    const terminar = () => {
      if (acabou) return;
      acabou = true;
      clearInterval(timers.current[ordemId]);
      delete timers.current[ordemId];
      setProcessando(({ [ordemId]: _, ...resto }) => resto);
      if (!montado.current) return;
      carregarEmpresas();
      carregarOrdens();
    };
    setProcessando((p) => ({ ...p, [ordemId]: { progresso: 'Iniciando…', rodada } }));
    timers.current[ordemId] = setInterval(() => {
      axios.get(`${URL_MON}/ordens/${ordemId}/`, { params: { _: Date.now() }, headers: headers() })
        .then((r) => {
          const o = r.data.ordem;
          // se a resposta do processar se perder (rede, proxy), o acompanhamento encerra sozinho
          if (o?.status === 'concluida' || o?.status === 'erro') { avisar(o); terminar(); return; }
          setProcessando((p) => (p[ordemId] ? { ...p, [ordemId]: { ...p[ordemId], progresso: `${o?.progresso || p[ordemId].progresso}${p[ordemId].rodada > 1 ? ` · rodada ${p[ordemId].rodada}` : ''}`, resultado: o?.resultado } } : p));
        })
        .catch(() => {});
    }, 3000);
    const rodar = () => {
      axios.post(`${URL_MON}/ordens/${ordemId}/processar/`, {}, { headers: headers(), timeout: 600000 })
        .then((r) => {
          if (acabou) return;
          const o = r.data.ordem;
          if (o?.status === 'pendente' && montado.current && rodada < 500) {
            rodada += 1;
            setProcessando((p) => (p[ordemId] ? { ...p, [ordemId]: { ...p[ordemId], rodada } } : p));
            carregarOrdens();
            rodar();
            return;
          }
          avisar(o);
          terminar();
        })
        .catch((e) => { if (!acabou) setErro(msgErro(e, 'Não foi possível processar a ordem')); terminar(); });
    };
    rodar();
  }, [carregarEmpresas, carregarOrdens]);

  const abrirNovaOrdem = (base) => {
    const [ini, fim] = mesAnterior();
    setNovaOrdem({ grupo_id: grupos[0]?.id || '', empresas: [], incluir_sp: false, sp_inicio: ini, sp_fim: fim, max_lotes: '', resultado: null, ...base });
  };

  const criarOrdem = () => {
    const corpo = {
      incluir_sp: novaOrdem.incluir_sp, sp_inicio: novaOrdem.sp_inicio, sp_fim: novaOrdem.sp_fim,
      max_lotes: novaOrdem.max_lotes ? Number(novaOrdem.max_lotes) : undefined,
      ...(novaOrdem.modo === 'grupo' ? { grupo_id: novaOrdem.grupo_id } : { empresas: novaOrdem.empresas.map((e) => e.cod) }),
    };
    setCriando(true);
    axios.post(`${URL_MON}/ordens/`, corpo, { headers: headers() })
      .then((r) => { setNovaOrdem((s) => ({ ...s, resultado: r.data })); carregarEmpresas(); carregarOrdens(); })
      .catch((e) => {
        if (e.response?.data?.recusadas) setNovaOrdem((s) => ({ ...s, resultado: e.response.data }));
        else setErro(msgErro(e, 'Não foi possível criar as ordens'));
      })
      .finally(() => setCriando(false));
  };

  const grupoSel = grupos.find((g) => g.id === novaOrdem?.grupo_id);

  return (
    <div className="painel-op fz-app">
      <div className="container-fluid mt-4 shadow p-4 rounded bg-white">
        <div className="header-wrapper d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
          <div className="d-flex align-items-center">
            <i className="fas fa-satellite-dish header-icon me-2" aria-hidden="true"></i>
            <h2 className="titulo-pagina m-0">Monitor de NFS-e</h2>
          </div>
          <div className="gr-toolbar">
            <button type="button" className="gr-btn" onClick={recarregarTudo} disabled={carregando}>
              <i className={`fas ${carregando ? 'fa-spinner fa-spin' : 'fa-rotate'}`} aria-hidden="true"></i>Atualizar
            </button>
          </div>
        </div>

        {erro && <div className="alert alert-danger py-2" role="button" onClick={() => setErro('')}><i className="fas fa-circle-exclamation me-2"></i>{erro}</div>}
        {msg && <div className="alert alert-success py-2" role="button" onClick={() => setMsg('')}><i className="fas fa-circle-check me-2"></i>{msg}</div>}

        <section className="po-card mb-3">
          <nav className="po-tabs" role="tablist">
            {ABAS.map((a) => (
              <button key={a.id} type="button" role="tab" aria-selected={aba === a.id} className={`po-tab ${aba === a.id ? 'ativa' : ''}`} onClick={() => setAba(a.id)}>
                <i className={`fas ${a.ico}`}></i>{a.txt}
              </button>
            ))}
          </nav>
        </section>

        {carregando && !dados ? (
          <div className="text-center py-5 text-muted"><i className="fas fa-spinner fa-spin fa-3x mb-3" style={{ color: '#00b5cc' }}></i><div className="fs-5">Carregando o Monitor...</div></div>
        ) : (
          <>
            {aba === 'empresas' && (
              <AbaEmpresas dados={dados} grupos={grupos} carregar={carregarEmpresas} setErro={setErro} setMsg={setMsg}
                abrirNovaOrdem={abrirNovaOrdem} ordens={ordens} carregarOrdens={carregarOrdens} processar={processar} processando={processando} />
            )}
            {aba === 'grupos' && (
              <AbaGrupos grupos={grupos} setGrupos={setGrupos} setErro={setErro} setMsg={setMsg} abrirNovaOrdem={abrirNovaOrdem} recarregarEmpresas={carregarEmpresas} />
            )}
            {aba === 'documentos' && <AbaDocumentos todasEmpresas={todasEmpresas} setErro={setErro} />}
            {aba === 'retroativas' && <AbaRetroativas todasEmpresas={todasEmpresas} setErro={setErro} />}
          </>
        )}

        {novaOrdem && (
          <Modal titulo="Nova ordem de download" icone="fa-download" onFechar={() => setNovaOrdem(null)} travado={criando}
            subtitulo={novaOrdem.modo === 'empresa' ? novaOrdem.empresas.map((e) => `${e.cod} · ${e.cliente}`).join(', ') : grupoSel ? `Grupo ${grupoSel.nome} (${grupoSel.total_empresas} empresa(s))` : 'Por grupo'}
            rodape={novaOrdem.resultado ? (
              <>
                <button className="btn btn-secondary me-auto" onClick={() => setNovaOrdem(null)}><i className="fas fa-times me-2"></i>Fechar</button>
                {(novaOrdem.resultado.criadas || []).length === 1 && novaOrdem.resultado.criadas[0].provedor === 'nacional' && (
                  <button className="btn btn-success" onClick={() => { processar(novaOrdem.resultado.criadas[0].id); setNovaOrdem(null); setAba('empresas'); }}>
                    <i className="fas fa-play me-2"></i>Processar agora
                  </button>
                )}
              </>
            ) : (
              <>
                <button className="btn btn-secondary me-auto" onClick={() => setNovaOrdem(null)} disabled={criando}><i className="fas fa-times me-2"></i>Cancelar</button>
                <button className="btn btn-primary" onClick={criarOrdem} disabled={criando || (novaOrdem.modo === 'grupo' && !novaOrdem.grupo_id) || (novaOrdem.incluir_sp && (!novaOrdem.sp_inicio || !novaOrdem.sp_fim))}>
                  <i className={`fas ${criando ? 'fa-spinner fa-spin' : 'fa-paper-plane'} me-2`}></i>Criar ordem
                </button>
              </>
            )}>
            {novaOrdem.resultado ? (
              <>
                <div className="fw-bold mb-2">{novaOrdem.resultado.mensagem}</div>
                {(novaOrdem.resultado.criadas || []).map((o) => (
                  <div key={o.id} className="small"><i className="fas fa-circle-check text-success me-2"></i>{o.cod} · {o.cliente} — {PROVEDORES[o.provedor]}</div>
                ))}
                {(novaOrdem.resultado.recusadas || []).map((o) => (
                  <div key={`${o.cod}-${o.provedor}`} className="small"><i className="fas fa-circle-xmark text-danger me-2"></i>{o.cod} · {o.cliente || ''} — {PROVEDORES[o.provedor]}: {o.motivo}</div>
                ))}
                {(novaOrdem.resultado.criadas || []).length > 0 && <div className="fz-sub mt-3">As ordens entram na fila e o processamento automático pega em até 30 minutos. Use "Processar agora" para não esperar.</div>}
              </>
            ) : (
              <>
                {novaOrdem.modo === 'grupo' && (
                  <>
                    <label className="form-label small fw-bold mb-1">Grupo</label>
                    <select className="form-select mb-3" value={novaOrdem.grupo_id} onChange={(e) => setNovaOrdem((s) => ({ ...s, grupo_id: e.target.value }))}>
                      {grupos.map((g) => <option key={g.id} value={g.id}>{g.nome} ({g.total_empresas})</option>)}
                    </select>
                  </>
                )}
                <div className="small mb-3"><i className="fas fa-satellite-dish me-2" style={{ color: '#00b5cc' }}></i>Nacional (ADN): continua do último NSU de cada empresa.</div>
                <label className="form-label small fw-bold mb-1">Limite de lotes (opcional)</label>
                <input className="form-control mb-1 fz-mono" inputMode="numeric" placeholder="sem limite" value={novaOrdem.max_lotes} onChange={(e) => setNovaOrdem((s) => ({ ...s, max_lotes: e.target.value.replace(/\D/g, '') }))} />
                <div className="fz-sub mb-3">Cada lote traz até 50 documentos. Útil para a primeira carga de uma empresa com muitas notas.</div>
                <label className="po-toggle mb-2"><input type="checkbox" checked={novaOrdem.incluir_sp} onChange={(e) => setNovaOrdem((s) => ({ ...s, incluir_sp: e.target.checked }))} />Incluir São Paulo (prefeitura)</label>
                {novaOrdem.incluir_sp && (
                  <div className="fz-conferencia">
                    <div className="row g-2">
                      <div className="col"><label className="form-label small fw-bold mb-1">De</label><input type="date" className="form-control" value={novaOrdem.sp_inicio} onChange={(e) => setNovaOrdem((s) => ({ ...s, sp_inicio: e.target.value }))} /></div>
                      <div className="col"><label className="form-label small fw-bold mb-1">Até</label><input type="date" className="form-control" value={novaOrdem.sp_fim} onChange={(e) => setNovaOrdem((s) => ({ ...s, sp_fim: e.target.value }))} /></div>
                    </div>
                    <div className="fz-sub mt-2">O CCM vai na ordem só quando a empresa é de São Paulo (cidade e UF na Base Fiscal) e tem IM cadastrada.</div>
                  </div>
                )}
              </>
            )}
          </Modal>
        )}
      </div>
    </div>
  );
}
